import { ref, shallowRef, computed, watch, nextTick, Ref, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { Duration } from 'luxon';
import { cloneDeep, isEqual } from 'lodash';
import { ApiDuty, Qualification } from '@/rennovation/planning-view/types/api/duties-response';

export interface BreakItem {
	start: number;
	end: number;
	duration: number;
	paid: boolean;
}

export interface DutyState {
	id: string | null;
	root: string | null;
	name: string | null;
	type: string | null;
	user: any;
	qualifications: Qualification[] | string[];
	start: string | null;
	startStop: string | null;
	end: string | null;
	endStop: string | null;
	standbyStart: string | null;
	standbyEnd: string | null;
	additionalWorkTime: string | null;
	workTime: string | number | null;
	labels: any[];
	notes: string | null;
	extraPayAccountQuantity: Record<string, number>;
	breaks: any[];
	provider: string;
	providerId: string;
	original: string | null;
	preview: string | null;
}

export interface PermissionResult {
	hasEditPermission: boolean;
	editableField: string[];
	canPartialEdit: boolean;
}

export type RuleFunction = (v: string) => boolean | string;

export interface VuetifyForm {
	validate: () => Promise<{ valid: boolean; errors: any[] }>;
	reset: () => void;
	resetValidation: () => void;
}

export interface VuetifyField {
	validate: () => Promise<any>;
}

export interface UseDutyFormProps {
	dutyID: string;
	isSpecific?: boolean;
	fieldsToDisable: string[];
}

import useDateTime from '../../../../../../../components/composables/useDateTime.js';
import { useDutyFormValidator } from '@/rennovation/planning-view/composables/useDutyFormValidator';
import { useDutyStore, usePlanningStore } from '@/rennovation/planning-view/store';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { useUIStore } from '@/rennovation/planning-view/store';
import { useUserActionsBridgeStore } from '@/rennovation/planning-view/store/userActionsBridge';
import { useUserStore } from '@/rennovation/planning-view/store/useUserStore';
import { usePlanningDayStatus } from '@/rennovation/planning-view/composables/useDayStatus';
import { useDayStore } from '@/rennovation/planning-view/store/useDayStore';

export function useDutyForm(props: UseDutyFormProps, emit: (event: string, ...args: any[]) => void) {
	const { t } = useI18n();
	const { formatSecondsDuration, formatTimeToSeconds, formatDurationToSeconds } = useDateTime();
	const { evaluateEditPermissions } = useDutyFormValidator() as {
		evaluateEditPermissions: (date: string, duty: any, activities: any) => PermissionResult;
	};

	const dutyStore = useDutyStore();
	const planningStore = usePlanningStore();
	const dayStore = useDayStore();
	const planningBridge = usePlanningBridgeStore();
	const userStore = useUserStore();
	const userActionsBridge = useUserActionsBridgeStore();
	const uiStore = useUIStore();

	const createDutyState = (): DutyState => ({
		id: null,
		root: null,
		name: null,
		type: null,
		user: null,
		qualifications: [],
		start: null,
		startStop: null,
		end: null,
		endStop: null,
		standbyStart: null,
		standbyEnd: null,
		additionalWorkTime: null,
		workTime: null,
		labels: [],
		notes: null,
		extraPayAccountQuantity: {},
		breaks: [],
		provider: '',
		providerId: '',
		original: null,
		preview: null,
	});

	const loadingState = ref<boolean>(true);
	const duty = shallowRef<ApiDuty | null>(null);
	const rootDuties = computed(() => {
		const currentDutyId = duty.value?.id;
		const duties = dutyStore.dutiesWithSameRootID;
		if (!currentDutyId || !Array.isArray(duties)) return [];

		return duties.filter((d) => d?.id !== currentDutyId);
	});
	const openedDetailsPanel = ref<number[]>([0]);
	const openedExtraPayPanel = ref<number[]>([0]);
	const openedTimeAllowancePanel = ref<number[]>([0]);
	const rootDutyDialog = ref<boolean>(false);
	const breakStart = ref<VuetifyField[]>([]);
	const breakEnd = ref<VuetifyField[]>([]);
	const workTimeDb = ref<string>(null);

	const hasEditPermission = ref<boolean>(false);
	const editableField = ref<string[]>([]);
	const canPartialEdit = ref<boolean>(false);

	const extraPayAccountQuantityLocal = ref<Record<string, string | number>>({});
	const rawDuty = ref<DutyState>(createDutyState());
	const changedDuty = ref<DutyState>(createDutyState());
	const showMergeDutyModal = ref<boolean>(false);

	const rawBreaks = ref<BreakItem[]>([]);
	const breaksState = ref<BreakItem[]>([]);

	const outerSaveButtonClicked = ref<boolean>(false);
	const showCommentDialog = ref<boolean>(false);
	const changeComment = ref<string>('');
	const selectedRootDuties = shallowRef<ApiDuty[]>([]);
	const detailsForm = ref<VuetifyForm | null>(null);

	const { isPastOrPublishedDay } = usePlanningDayStatus(duty, dayStore.daysRef);

	const allDriversData = computed(() => userStore.currentDayDrivers?.data);
	const qualifications = computed(() => userActionsBridge.qualifications || []);
	const getDutyId = computed(() => duty.value?.id);

	// ============================================
	// QUALIFICATIONS TOGGLE HELPERS
	// ============================================
	const selectedQualificationIds = computed<string[]>(() => {
		const currentVals = changedDuty.value?.qualifications || [];
		return currentVals.map((item: any) => (typeof item === 'object' && item !== null ? item.id || item._id : item));
	});

	const allQualificationsSelected = computed(() => {
		const available = qualifications.value;
		if (!available || available.length === 0) return false;
		return selectedQualificationIds.value.length === available.length;
	});

	const someQualificationsSelected = computed(() => {
		const selectedCount = selectedQualificationIds.value.length;
		const availableCount = qualifications.value?.length || 0;
		return selectedCount > 0 && selectedCount < availableCount;
	});

	const toggleSelectAllQualifications = () => {
		if (allQualificationsSelected.value) {
			changedDuty.value.qualifications = [];
		} else {
			const available = qualifications.value || [];
			changedDuty.value.qualifications = available.map((q: any) => q.id || q._id);
		}
	};

	const typeList = computed(() => {
		const base = [
			{ id: 'duty', name: t('duty_dayType_duty') },
			{ id: 'reserve', name: t('duty_dayType_reserve') },
			{ id: 'compensation', name: t('duty_dayType_compensation') },
			{ id: 'rest', name: t('duty_dayType_rest') },
		];
		if (duty.value?.type === 'absence') {
			base.push({ id: 'absence', name: t('duty_dayType_absence') });
		}
		return base;
	});

	const isAffectedByPreview = computed(() => {
		const id = props.dutyID;
		return !!(
			duty.value?.affectedByAnyPreview ||
			dutyStore.dutiesMappedById[id]?.affectedByAnyPreview ||
			dutyStore.unallocatedDutiesById[id]?.affectedByAnyPreview
		);
	});

	const breaks = computed<BreakItem[]>(() => {
		const mappedBreaks = cloneDeep(breaksState.value) ?? [];
		return mappedBreaks.map(({ start, end, duration, paid }) => ({
			start: formatSecondsDuration(start) || '',
			end: formatSecondsDuration(end) || '',
			duration: formatSecondsDuration(duration) || formatSecondsDuration(end - start) || '',
			paid,
		}));
	});

	const formatExtraPay = (value?: number): string | undefined => {
		if (value === undefined) return;
		const flooredValue = Math.floor(value);
		const remainingValue = Math.round((value - flooredValue) * 100);
		return `${flooredValue}.${remainingValue.toString().padStart(2, '0')}`;
	};

	const extraPayNight1 = computed(() => formatExtraPay(planningStore.singleStatistics?.extraPayNight1));
	const extraPayNight2 = computed(() => formatExtraPay(planningStore.singleStatistics?.extraPayNight2));
	const extraPaySaturday = computed(() => formatExtraPay(planningStore.singleStatistics?.extraPaySaturday));
	const extraPaySunday = computed(() => formatExtraPay(planningStore.singleStatistics?.extraPaySunday));
	const commentDialogTitle = computed(() => t('add_comment_title', { module: t('duty_lowercase') }));

	const isDutyType = (type: string): boolean => changedDuty.value?.type === type;

	const canEdit = computed(() => hasEditPermission.value);

	const requireDuty = computed(() => canEdit.value && isDutyType('duty'));

	const requireDutyOrReserve = computed(() => canEdit.value && (isDutyType('duty') || isDutyType('reserve')));
	const requireDutyOrAbsence = computed(() => {
		if (!hasEditPermission.value) {
			return false;
		}

		return isDutyType('duty') || isDutyType('absence');
	});

	const changedFields = computed(() => {
		const baseChanges = (Object.keys(changedDuty.value) as Array<keyof DutyState>).reduce(
			(acc, key) => {
				if (changedDuty.value[key] !== rawDuty.value[key]) {
					(acc as any)[key] = changedDuty.value[key];
				}
				return acc;
			},
			{} as Record<string, unknown>
		);

		if (!isEqual(rawBreaks.value, breaksState.value)) {
			baseChanges.breaks = breaksState.value;
		}

		return baseChanges;
	});

	const hasChanges = computed(() => !isEqual(rawDuty.value, changedDuty.value) || !isEqual(rawBreaks.value, breaksState.value));
	const hasCurrentDayDuties = computed(() => dutyStore.currentDayDuties.data.all.length > 0);

	const isTimeFieldDisabled = (fieldKey: string): boolean => {
		if (editableField.value?.includes(fieldKey)) {
			return false;
		}
		if (!requireDutyOrAbsence.value) {
			return true;
		}
		return isDisabled(fieldKey);
	};

	const parseTime = (timeStr: string): number => {
		if (!timeStr) return 0;
		const [hours, minutes] = timeStr.split(':').map(Number);
		return hours * 60 + minutes;
	};

	const convertNumberToTime = (value: string): string => {
		if (!value) return value;
		if ((value.length === 3 || value.length === 4) && !value.includes(':') && !value.includes('.') && !value.includes(',')) {
			if (value.length === 3) return `0${value.substring(0, 1)}:${value.substring(1, 3)}`;
			return `${value.substring(0, 2)}:${value.substring(2, 4)}`;
		}
		if (value.length === 4 && (value.charAt(1) === ':' || value.charAt(1) === ',' || value.charAt(1) === '.')) {
			return `0${value.substring(0, 1)}:${value.substring(2, 4)}`;
		}
		if (value.length === 5) return `${value.substring(0, 2)}:${value.substring(3, 5)}`;
		return value;
	};

	const timeFormatRule: RuleFunction = (v) => !v || /^[0-3]?[0-9]:[0-5][0-9]$/.test(v) || t('planning_wrong_time_format');

	const hasAnyValueRule = (): boolean | string => {
		if (!requireDuty.value) {
			return true;
		}

		if (changedDuty.value.start || changedDuty.value.end || changedDuty.value.standbyStart || changedDuty.value.standbyEnd) {
			return true;
		}
		return t('planning_at_least_one_time_pair_required');
	};

	const requiredIfCounterpartRuleFactory = (counterpart: string): (() => boolean | string) => {
		return () => {
			let currentValue: string | null = '';
			let counterpartValue: string | null = '';
			switch (counterpart) {
				case 'start':
					currentValue = changedDuty.value.end;
					counterpartValue = changedDuty.value.start;
					break;
				case 'end':
					currentValue = changedDuty.value.start;
					counterpartValue = changedDuty.value.end;
					break;
				case 'standbyStart':
					currentValue = changedDuty.value.standbyEnd;
					counterpartValue = changedDuty.value.standbyStart;
					break;
				case 'standbyEnd':
					currentValue = changedDuty.value.standbyStart;
					counterpartValue = changedDuty.value.standbyEnd;
					break;
			}
			if (currentValue) return true;
			if (counterpartValue) return t('planning_duty_details_please_choose_time');
			return true;
		};
	};

	const timeOrderRule = (counterpart: string): ((v: string) => boolean | string) => {
		return (v) => {
			if (!v) return true;
			let startTime: string | null = null;
			let endTime: string | null = null;
			switch (counterpart) {
				case 'start':
				case 'end':
					startTime = changedDuty.value.start;
					endTime = changedDuty.value.end;
					break;
				case 'standbyStart':
				case 'standbyEnd':
					startTime = changedDuty.value.standbyStart;
					endTime = changedDuty.value.standbyEnd;
					break;
			}
			if (!startTime || !endTime) return true;
			if (parseTime(startTime) > parseTime(endTime)) return t(`planning_start_time_must_be_before_end_time_${counterpart}`);
			return true;
		};
	};

	const timeRuleFactory = (counterpart: string) => [
		timeFormatRule,
		requiredIfCounterpartRuleFactory(counterpart),
		hasAnyValueRule,
		timeOrderRule(counterpart),
	];

	const nameRules = [(v: unknown) => (typeof v === 'string' && v.trim().length > 0) || t('planning_not_empty')];

	const isBreakOverlapping = (currentIndex: number): boolean => {
		const breaks = breaksState.value;
		const currentBreak = breaks[currentIndex];

		if (!currentBreak || currentBreak.start === undefined || currentBreak.end === undefined || currentBreak.start > currentBreak.end) {
			return false;
		}

		const currentStart = Number(currentBreak.start);
		const currentEnd = Number(currentBreak.end);

		for (let i = 0; i < breaks.length; i++) {
			if (i === currentIndex) continue;

			const other = breaks[i];

			if (other.start === undefined || other.end === undefined || other.start > other.end) {
				continue;
			}

			const otherStart = Number(other.start);
			const otherEnd = Number(other.end);

			if (currentStart <= otherEnd && otherStart <= currentEnd) {
				return true;
			}
		}

		return false;
	};

	const breakOverlappingRule = (index: number): (() => boolean | string) => {
		return () => (isBreakOverlapping(index) ? t('planning_breaks_overlap') : true);
	};

	const requiredBreakTimeRule = (counterpart: string, index: number): (() => boolean | string) => {
		return () => {
			const breakItem = breaks.value[index];
			if (!breakItem) return true;
			const currentValue = counterpart === 'start' ? breakItem.end : breakItem.start;
			const counterpartValue = counterpart === 'start' ? breakItem.start : breakItem.end;
			if (currentValue) return true;
			if (counterpartValue || (!currentValue && !counterpartValue)) return t('planning_breaks_please_choose_time');
			return true;
		};
	};

	const breakTimeOrderRule = (counterpart: string, index: number): ((v: string) => boolean | string) => {
		return (v) => {
			if (!v) return true;
			const startTime = breaks.value[index]?.start;
			const endTime = breaks.value[index]?.end;
			if (!startTime || !endTime) return true;
			if (parseTime(startTime) > parseTime(endTime)) return t(`planning_start_time_must_be_before_end_time_${counterpart}`);
			return true;
		};
	};

	const breakBoundaryRule = (counterpart: string): ((v: string) => boolean | string) => {
		return (v) => {
			if (!v) return true;
			// counterpart is the OTHER field: start field passes 'end', end field passes 'start'
			if (counterpart === 'end') {
				const dutyStart = changedDuty.value.start;
				if (!dutyStart) return true;
				if (parseTime(v) < parseTime(dutyStart)) return t('planning_break_starts_before_duty');
			} else {
				const dutyEnd = changedDuty.value.end;
				if (!dutyEnd) return true;
				if (parseTime(v) > parseTime(dutyEnd)) return t('planning_break_ends_after_duty');
			}
			return true;
		};
	};

	const breakValidationRule = (counterpart: string, index: number) => [
		timeFormatRule,
		requiredBreakTimeRule(counterpart, index),
		breakTimeOrderRule(counterpart, index),
		breakOverlappingRule(index),
		breakBoundaryRule(counterpart),
	];

	const validateBreakFields = async (index: number): Promise<void> => {
		await nextTick();
		breakStart.value?.[index]?.validate?.();
		breakEnd.value?.[index]?.validate?.();
	};

	const updateTime = (): void => {
		const unpaidBreakTime = (breaksState.value || []).reduce((acc, brk) => (!brk.paid && brk.duration ? acc + brk.duration : acc), 0);

		const prevUnpaidBreakTime = (rawDuty.value.breaks || []).reduce((acc, brk) => (!brk.paid && brk.duration ? acc + brk.duration : acc), 0);

		const calcWorkTimeSeconds = Math.max(
			0,
			formatTimeToSeconds(changedDuty.value.end) -
				formatTimeToSeconds(changedDuty.value.start) -
				unpaidBreakTime +
				formatDurationToSeconds(changedDuty.value.additionalWorkTime)
		);

		const rawWorkTimeSeconds = Math.max(
			0,
			formatTimeToSeconds(rawDuty.value.end) -
				formatTimeToSeconds(rawDuty.value.start) -
				prevUnpaidBreakTime +
				formatDurationToSeconds(rawDuty.value.additionalWorkTime)
		);

		const newWorkTime = formatSecondsDuration(calcWorkTimeSeconds);
		const rawWorkTime = formatSecondsDuration(rawWorkTimeSeconds);

		if (newWorkTime !== rawWorkTime) {
			changedDuty.value.workTime = newWorkTime;
		} else {
			changedDuty.value.workTime = rawDuty.value.workTime;
		}
	};
	const changedDutyProperties = computed(() => {
		const changes: Array<{ property: string; value: unknown }> = [];

		// Compare each property between changedDuty and rawDuty
		(Object.keys(changedDuty.value) as Array<keyof DutyState>).forEach((key) => {
			if (!isEqual(changedDuty.value[key], rawDuty.value[key])) {
				changes.push({
					property: key,
					value: changedDuty.value[key],
				});
			}
		});

		// Check if break times/items were modified
		if (!isEqual(rawBreaks.value, breaksState.value)) {
			changes.push({
				property: 'breaks',
				value: breaksState.value,
			});
		}

		return changes;
	});

	const showCommentDialogFields = computed(() => {
		const CRITICAL_TIME_FIELDS = new Set(['additionalWorkTime', 'workTime', 'end', 'start', 'breaks', 'standbyEnd', 'standbyStart']);

		return changedDutyProperties.value.some((change) => CRITICAL_TIME_FIELDS.has(change.property));
	});

	watch(() => [changedDuty.value.start, changedDuty.value.end, changedDuty.value.additionalWorkTime], updateTime);

	const handlePaidChange = (index: number, val: boolean) => {
		if (breaksState.value[index]) {
			breaksState.value[index].paid = val;
		}

		breakTimeChange(index);
	};

	const breakTimeChange = (index: number): void => {
		const item = breaks.value[index];
		if (!item) return;

		item.start = convertNumberToTime(item.start);
		item.end = convertNumberToTime(item.end);

		if (item.start && item.end) {
			const startSec = formatTimeToSeconds(item.start || '');
			const endSec = formatTimeToSeconds(item.end || '');

			const currentBreakItem = breaksState.value[index];

			if (!currentBreakItem) return;

			if (endSec >= startSec) {
				const duration = Duration.fromObject({ seconds: endSec - startSec }).toFormat('hh:mm');

				breaksState.value[index] = {
					start: startSec,
					end: endSec,
					duration: formatTimeToSeconds(duration || ''),
					paid: currentBreakItem.paid,
				};
				updateTime();
			}
		}
		validateBreakFields(index);
	};

	const addNewBreak = (): void => {
		breaksState.value = [...breaksState.value, { start: 0, end: 0, duration: 0, paid: false }];
	};

	const removeBreak = (index: number): void => {
		const newBreaks = [...breaksState.value];
		newBreaks.splice(index, 1);
		breaksState.value = newBreaks;
		updateTime();
	};

	const isDisabled = (input: string): boolean => {
		if (Array.isArray(editableField.value) && editableField.value.includes(input)) {
			return false;
		}

		if (hasEditPermission.value) {
			return props.fieldsToDisable.includes(input);
		}

		return true;
	};

	const updateExtraPayAccountQuantity = (key: string): void => {
		const updatedMap = { ...changedDuty.value.extraPayAccountQuantity };
		const rawVal = extraPayAccountQuantityLocal.value[key];

		if (rawVal !== null && rawVal !== undefined && rawVal !== '') {
			const numValue = typeof rawVal === 'number' ? rawVal : parseFloat(String(rawVal).trim());

			if (!isNaN(numValue)) {
				updatedMap[key] = numValue;
			} else {
				delete updatedMap[key];
			}
		} else {
			delete updatedMap[key];
		}

		changedDuty.value.extraPayAccountQuantity = updatedMap;
	};

	const triggerUpdate = async (isOuterSave = true): Promise<void> => {
		outerSaveButtonClicked.value = isOuterSave;
		if (!detailsForm.value) return;
		const response = await detailsForm.value.validate();
		if (!response.valid) {
			return;
		}

		if (rootDuties.value.length > 0 && hasChanges.value) rootDutyDialog.value = true;
		else {
			if (isPastOrPublishedDay.value && showCommentDialogFields.value) {
				showCommentDialog.value = true;
			} else {
				handleSubmit();
			}
		}
	};

	const closeCommentDialog = (): void => {
		showCommentDialog.value = false;
	};

	const handleRootDuties = (payload: ApiDuty[]): void => {
		selectedRootDuties.value = payload;
		if (isPastOrPublishedDay.value && showCommentDialogFields.value) {
			showCommentDialog.value = true;
			return;
		}

		handleSubmit();
	};

	const prepareDutyPayload = (basePayload: Record<string, any>) => {
		const payload = { ...basePayload };

		const pairs = [
			['start', 'end'],
			['standbyStart', 'standbyEnd'],
		] as const;

		for (const [startKey, endKey] of pairs) {
			const hasStart = payload[startKey] !== undefined;
			const hasEnd = payload[endKey] !== undefined;

			if (hasStart || hasEnd) {
				if (!hasStart) payload[startKey] = changedDuty.value[startKey];
				if (!hasEnd) payload[endKey] = changedDuty.value[endKey];
			}
		}

		const timeKeys = ['start', 'end', 'workTime', 'standbyStart', 'standbyEnd', 'additionalWorkTime'] as const;
		for (const key of timeKeys) {
			if (payload[key] === '') {
				payload[key] = null;
				continue;
			}
			if (payload[key] !== undefined) {
				payload[key] = formatTimeToSeconds(payload[key]);
			}
		}

		return payload;
	};

	const handleMutipleUpdates = async (): Promise<void> => {
		const baseFields = {
			...changedFields.value,
			changeComment: changeComment.value,
		};

		const payload = selectedRootDuties.value.map((d) => ({
			id: d._id,
			...prepareDutyPayload(baseFields),
		}));

		await dutyStore.updateRootDuties(payload);
	};

	const handleSubmit = async (comment: string = ''): Promise<void> => {
		changeComment.value = comment;

		const dutyID = changedDuty.value?.id;
		if (!dutyID) return;

		const baseFields = { ...changedFields.value, changeComment: comment };
		const payload = prepareDutyPayload(baseFields);
		const response = await dutyStore.updateDuty(dutyID, payload);

		emit('duty-updated', response);

		if (selectedRootDuties.value.length > 0 && rootDutyDialog.value) {
			await handleMutipleUpdates();
		}

		await resetAfterSave();
		handlePostSaveNavigation();
	};

	async function resetAfterSave(): Promise<void> {
		showCommentDialog.value = false;
		selectedRootDuties.value = [];
		rootDutyDialog.value = false;
		if (props.dutyID) {
			const freshDuty = await dutyStore.fetchDutyByID(props.dutyID);
			syncLocalState(freshDuty);
		}
	}

	const mapDutyToState = (dutyData: ApiDuty): DutyState => {
		const {
			id,
			root,
			name,
			type,
			user,
			qualifications,
			standbyStart,
			standbyEnd,
			additionalWorkTime,
			workTime,
			start,
			end,
			startStop,
			endStop,
			labels,
			notes,
			extraPayAccountQuantity,
			breaks: extractedBreaks,
			original,
			preview,
		} = dutyData;

		return {
			id,
			root,
			name,
			type,
			user,
			qualifications: qualifications || [],
			startStop,
			endStop,
			labels: labels || [],
			notes,
			extraPayAccountQuantity: extraPayAccountQuantity || {},
			start: formatSecondsDuration(start),
			end: formatSecondsDuration(end),
			standbyStart: formatSecondsDuration(standbyStart),
			standbyEnd: formatSecondsDuration(standbyEnd),
			additionalWorkTime: formatSecondsDuration(additionalWorkTime),
			workTime: formatSecondsDuration(workTime),
			breaks: extractedBreaks || [],
			original: original ?? null,
			preview: preview ?? null,
		};
	};

	function syncLocalState(dutyData: ApiDuty) {
		const freshState = mapDutyToState(dutyData);

		rawDuty.value = cloneDeep(freshState);
		changedDuty.value = cloneDeep(freshState);

		rawBreaks.value = cloneDeep(dutyData.breaks || []);
		breaksState.value = cloneDeep(dutyData.breaks || []);

		extraPayAccountQuantityLocal.value = cloneDeep(dutyData.extraPayAccountQuantity || {});

		updateTime();
		// rawDuty.value.workTime = changedDuty.value.workTime;

		workTimeDb.value = formatSecondsDuration(dutyData.workTime);
	}

	async function handleRootDialogCancel(): Promise<void> {
		selectedRootDuties.value = [];
		rootDutyDialog.value = false;
	}

	function handlePostSaveNavigation(): boolean {
		if (uiStore.tabClicked) {
			emit('handleTabChange');
			return true;
		}

		if (outerSaveButtonClicked.value) {
			emit('close', false);
			return true;
		}

		return false;
	}

	const init = async (dutyID: string): Promise<void> => {
		const [dutyResponse, dutyActivities] = await Promise.all([dutyStore.fetchDutyByID(dutyID), dutyStore.loadDutyActivities(dutyID)]);

		duty.value = Object.freeze(structuredClone(dutyResponse)) as ApiDuty;
		let date = dutyResponse.dayObject.dateString;
		let users = dutyResponse.user;
		let secondaryFetches: Promise<any>[] = [];
		if (users) {
			secondaryFetches = [planningStore.loadSingleStatistics(date, users)];
		}

		const rootId = dutyResponse.root;

		if (rootId) {
			secondaryFetches.push(dutyStore.loadDutiesWithRootID(rootId, dutyID));
		}

		let driversPayload: any = null;
		if (dutyResponse.dayObject) {
			secondaryFetches.push(userActionsBridge.fetchQualifications());
			if (userStore.currentDayDrivers.data.all.length === 0 || userStore.currentDayDrivers.day !== duty.value.day) {
				secondaryFetches.push(
					userStore.loadAvailableDriversByDay(dutyResponse.dayObject.dateString).then((res: any) => {
						driversPayload = res;
					})
				);
			}

			if (!hasCurrentDayDuties.value || dutyStore.currentDayDuties.day !== duty.value.day) {
				secondaryFetches.push(dutyStore.fetchAvailableDutiesByDay(dutyResponse.dayObject.dateString));
			}
		}

		await Promise.all(secondaryFetches);

		const {
			id,
			root,
			name,
			type,
			user,
			qualifications,
			standbyStart,
			standbyEnd,
			additionalWorkTime,
			workTime,
			start,
			end,
			startStop,
			endStop,
			labels,
			notes,
			extraPayAccountQuantity,
			breaks: extractedBreaks,
			provider,
			providerId,
			original,
			preview,
		} = dutyResponse;

		const initialState: DutyState = {
			id,
			root,
			name,
			type,
			user,
			qualifications: qualifications || [],
			startStop,
			endStop,
			labels: labels || [],
			notes,
			extraPayAccountQuantity: extraPayAccountQuantity || {},
			start: formatSecondsDuration(start),
			end: formatSecondsDuration(end),
			standbyStart: formatSecondsDuration(standbyStart),
			standbyEnd: formatSecondsDuration(standbyEnd),
			additionalWorkTime: formatSecondsDuration(additionalWorkTime),
			workTime: formatSecondsDuration(workTime),
			breaks: extractedBreaks,
			provider,
			providerId,
			original: original ?? null,
			preview: preview ?? null,
		};

		rawDuty.value = { ...initialState };
		changedDuty.value = { ...initialState };
		if (Object.keys(extraPayAccountQuantity).length === 0) {
			extraPayAccountQuantityLocal.value = planningBridge.extraPayAccounts.reduce((acc, extraPayQ) => {
				acc[extraPayQ.number] = null;
				return acc;
			}, {});
		} else {
			extraPayAccountQuantityLocal.value = { ...extraPayAccountQuantity };
		}

		rawBreaks.value = cloneDeep(extractedBreaks) || [];
		breaksState.value = cloneDeep(extractedBreaks) || [];

		const permissionResult = evaluateEditPermissions(dutyResponse.dayObject.dateString, dutyResponse, dutyActivities);
		hasEditPermission.value = permissionResult.hasEditPermission;
		editableField.value = permissionResult.editableField;
		canPartialEdit.value = permissionResult.canPartialEdit;
		workTimeDb.value = formatSecondsDuration(dutyResponse.workTime);
		await nextTick();

		loadingState.value = false;
	};

	const close = () => {
		changedDuty.value = rawDuty.value;
		uiStore.setHasPendingChanges(false);
		emit('close', false);
	};

	watch(
		() => hasChanges.value,
		(newVal) => {
			uiStore.setHasPendingChanges(newVal);
		},
		{ immediate: true }
	);

	watch(
		() => [changedDuty.value.start, changedDuty.value.end, changedDuty.value.standbyStart, changedDuty.value.standbyEnd],
		async () => {
			await nextTick();
			if (detailsForm.value) {
				detailsForm.value.validate();
			}
		}
	);

	watch(
		() => breaksState.value,
		async () => {
			await nextTick();
			if (detailsForm.value) {
				detailsForm.value.validate();
			}
		},
		{ deep: true }
	);

	return {
		duty,
		loadingState,
		openedDetailsPanel,
		openedExtraPayPanel,
		openedTimeAllowancePanel,
		rootDuties,
		rootDutyDialog,
		breakStart,
		breakEnd,
		extraPayAccountQuantityLocal,
		rawDuty,
		changedDuty,
		breaksState,
		showCommentDialog,
		detailsForm,
		showMergeDutyModal,
		requireDutyOrReserve,
		requireDutyOrAbsence,
		allDriversData,
		qualifications,
		allQualificationsSelected,
		someQualificationsSelected,
		canPartialEdit,
		toggleSelectAllQualifications,
		typeList,
		breaks,
		requireDuty,
		hasChanges,
		changedFields,
		extraPayNight1,
		extraPayNight2,
		extraPaySaturday,
		extraPaySunday,
		commentDialogTitle,
		hasEditPermission,
		planningBridge,
		formatSecondsDuration,
		nameRules,
		workTimeDb,
		hasCurrentDayDuties,
		planningStore,
		getDutyId,
		isAffectedByPreview,
		handlePaidChange,
		close,
		createDutyState,
		isTimeFieldDisabled,
		handleRootDialogCancel,
		timeRuleFactory,
		breakValidationRule,
		breakTimeChange,
		validateBreakFields,
		isDisabled,
		addNewBreak,
		removeBreak,
		updateExtraPayAccountQuantity,
		triggerUpdate,
		closeCommentDialog,
		handleRootDuties,
		handleSubmit,
		init,
		uiStore,
	};
}
